import React, { useEffect, useState } from 'react';
import {
  FDAlert,
  FDStack,
} from '@facet-wealth/design';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useSnackbarContext } from 'Context/SnackbarContext';
import {
  DragDropPrioritization,
} from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/GoalPrioritization/components/DragDropPrioritization/DragDropPrioritization';
import { GoalsPageControlPanel } from 'Views/Page/Content/Templates/goalsPage/components/GoalsPageControlPanel';
import { useRefreshModalCollectionCleared } from 'Hooks/useRefreshEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { GoalItemGroup } from 'Views/Page/Content/Templates/goalsPage/components/GoalItemGroup';
import useUserContext from 'Context/UserContext';

const GoalsPageLayout = () => {
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshPageContentCollection, pageContentResource: resource } = usePageContentContext();
  const { isAgent } = useUserContext();
  const isMobile = useMobileLayout();
  useBoneBackgroundColor();

  const [prioritizing, setPrioritizing] = useState(false);
  const [goals, setGoals] = useState(resource?.attr('goals')?.resources()?.list() || []);
  const hasGroupedGoals = goals.some(goal => !!goal.attr('groupUUID'));
  const canGroup = isAgent;
  const showGroupAlert = !isAgent && hasGroupedGoals;

  useEffect(() => {
    setGoals(resource?.attr('goals')?.resources()?.list() || []);
  }, [resource.instanceId()]);

  const handleChange = (newGoals) => {
    resource.attr('goals').resources().removeAllItems();
    resource.attr('goals').resources().pushItems(newGoals);
    setGoals(newGoals);
  };

  const handleBreakGroup = async (newGoals) => {
    handleChange(newGoals);
    await resource.submit();
  };

  const handleToggleReprioritize = async () => {
    // If we are turning off prioritization, we need to save the changes
    if (prioritizing) {
      try {
        await resource.submit();
      } catch (e) {
        displayErrorMessage(e, e.errors || 'Unable to save new order. If the issue persists please contact your planner.');
        // Exit here so we don't set prioritizing to false if there is a failure
        return;
      }
    }
    setPrioritizing(!prioritizing);
  };

  const handleDelete = async (res) => {
    try {
      await res.delete();
      await refreshPageContentCollection();
    } catch (e) {
      displayErrorMessage(e, e.errors || 'An error occured while deleting goal. If the issue persists please contact your planner.');
    }
  };

  useRefreshModalCollectionCleared(async () => refreshPageContentCollection());

  return (
    <FDStack direction={'column'} alignItems={'center'} width={'100%'}>
      <FDStack direction={'column'} spacing={5} maxWidth={1432} width={'100%'} paddingX={2} paddingY={5}>
        <FDStack direction={'column'} spacing={2} width={'100%'} alignItems={'flex-start'}>
          <MetaControlField attr={'title'} />
          <MetaControlField attr={'subtitle'} />
        </FDStack>
        <FDStack direction={isMobile ? 'column' : 'row'} spacing={4} justifyContent={isMobile ? 'flex-start' : 'center'} width={'100%'} alignItems={'flex-start'}>
          {isMobile ? <GoalsPageControlPanel goalsPageResource={resource} prioritizing={prioritizing} onTogglePrioritizing={handleToggleReprioritize} /> : null}
          <FDStack direction={'column'} spacing={2} width={'100%'}>
            {showGroupAlert && (
              <FDAlert
                status={'info'}
                title={'You have grouped goals - goals are grouped to give equal weight in your roadmap, your planner can update if needed.'}
              />
            )}
            <DragDropPrioritization
              dropZoneId={'goals'}
              items={goals}
              ItemComponent={GoalItemGroup}
              onChange={handleChange}
              onBreakGroup={handleBreakGroup}
              disabled={!prioritizing}
              combineEnabled={canGroup}
              ItemComponentProps={{
                onDelete: handleDelete,
              }}
            />
          </FDStack>
          {isMobile ? null : <GoalsPageControlPanel goalsPageResource={resource} prioritizing={prioritizing} onTogglePrioritizing={handleToggleReprioritize} />}
        </FDStack>
      </FDStack>
    </FDStack>
  );
};

GoalsPageLayout.defaultProps = {};

GoalsPageLayout.propTypes = {};

export { GoalsPageLayout };
